import { Box, Flex, Icon } from '@fullstory/components';
import { useButton } from '@fullstory/components/hooks/useButton';
import { useElementSize } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, ReactElement, useMemo, useState } from 'react';
import css from './index.module.scss';

interface CarouselItemProps {
  testId: string;
  onClick?: () => void;
  /**
   * @default "base"
   */
  variant?: 'base' | 'brand';
}

export const CarouselItem: React.FC<PropsWithChildren<CarouselItemProps>> = ({
  testId = 'journey-template',
  children,
  variant = 'base',
}) => (
  <Box
    data-component="CarouselItem"
    testId={testId}
    className={classNames(css.carouselItem, {
      [css.variantBase]: variant === 'base',
      [css.variantBrand]: variant === 'brand',
    })}
  >
    {children}
  </Box>
);

export interface CarouselProps {
  testId?: string;
  itemheight: number;
  itemWidth: number;
  spaceBetweenItems?: number;
}

export const Carousel: React.FC<PropsWithChildren<CarouselProps>> = ({
  testId,
  itemheight,
  itemWidth,
  spaceBetweenItems = 32,
  children,
}) => {
  const [currentOffset, setCurrentOffset] = useState(0);
  const items = React.Children.toArray(children).filter(Boolean);
  const totalItems = items.length;

  const carouselPadding = 10;
  const stepSize = itemWidth + spaceBetweenItems;
  const [lastEvent, setLastEvent] = useState<'keyboard' | 'mouse' | null>(null);

  const { elementSize, ref } = useElementSize();

  const maxOffset = useMemo(() => {
    if (elementSize?.width) {
      return stepSize * totalItems - spaceBetweenItems - elementSize.width + 2 * carouselPadding;
    }
    return 0;
  }, [elementSize?.width, spaceBetweenItems, stepSize, totalItems]);

  const setOffsetWithCoerce = (requestedIndex: number) => {
    setCurrentOffset(Math.max(0, Math.min(maxOffset, requestedIndex)));
  };

  const moveLeft = () => {
    const leftIndex = Math.ceil(currentOffset / stepSize);
    setOffsetWithCoerce((leftIndex - 1) * stepSize);
  };

  const moveRight = () => {
    setOffsetWithCoerce(currentOffset + stepSize);
  };

  const moveToIndex = (index: number) => {
    setOffsetWithCoerce(index * stepSize);
  };

  const { getRootProps: getRootPropsCarouselItem, getClassNames: getClassNamesCarouselItem } =
    useButton('li');

  const { getRootProps: getRootPropsMoveLeft, getClassNames: getClassNamesMoveLeft } = useButton(
    'button',
    { tabIndex: -1 },
  );

  const { getRootProps: getRootPropsMoveRight, getClassNames: getClassNamesMoveRight } = useButton(
    'button',
    { tabIndex: -1 },
  );

  return (
    <section
      data-testid={testId}
      data-component="Carousel"
      ref={ref}
      className={classNames(css.carousel)}
      style={{ height: itemheight + 2 * carouselPadding, marginLeft: carouselPadding * -1 }}
    >
      {currentOffset > 0 && (
        <Flex
          justifyContent="center"
          className={classNames(css.navigation, css.leftNavigation)}
          testId="template-gallery-carousel-left-arrow"
        >
          <Box
            padding={12}
            {...getRootPropsMoveLeft({ onClick: () => moveLeft() })}
            className={getClassNamesMoveLeft({})}
          >
            <div className={classNames(css.iconContainer)}>
              <Icon name="chevron-left" size={16} />
            </div>
          </Box>
        </Flex>
      )}
      <ul
        className={classNames(css.innerCarousel)}
        style={{
          transform: `translateX(-${currentOffset}px)`,
          padding: carouselPadding,
        }}
      >
        {items.map((child, index) => {
          const carouselItem = React.cloneElement(child as ReactElement<CarouselItemProps>);
          return (
            <li
              key={carouselItem.props.testId}
              // The extra 2 accounts for the 1px borders on each side
              style={{
                height: itemheight + 2,
                width: itemWidth + 2,
                marginRight: spaceBetweenItems,
              }}
              {...getRootPropsCarouselItem({
                onClick: () => {
                  if (carouselItem.props.onClick) {
                    carouselItem.props.onClick();
                  }
                },
                onMouseDown: () => setLastEvent('mouse'),
                onKeyDown: () => setLastEvent('keyboard'),
                onFocus: () => {
                  if (lastEvent === 'keyboard') {
                    moveToIndex(index);
                  }
                },
              })}
              className={getClassNamesCarouselItem({}, css.carouselItemContainer)}
            >
              {carouselItem}
            </li>
          );
        })}
      </ul>
      {currentOffset < maxOffset && (
        <Flex
          justifyContent="center"
          className={classNames(css.navigation, css.rightNavigation)}
          testId="template-gallery-carousel-right-arrow"
        >
          <Box
            padding={12}
            {...getRootPropsMoveRight({ onClick: () => moveRight() })}
            className={getClassNamesMoveRight({})}
          >
            <div className={classNames(css.iconContainer)}>
              <Icon name="chevron-right" size={16} />
            </div>
          </Box>
        </Flex>
      )}
    </section>
  );
};
